import React from 'react';
import { get } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { FieldArrayInputProps } from '../physical-person-input';
import { InputText } from '../../../../../input';
import { RHFInputText } from '../../../../../../packages/react-hook-form';
import { MaskedSwedishSsnInput } from '../../../../../input/components';
import { MaskedSwedishPostNumberInput } from '../../../../../input/components/masked-swedish-postnumber-input';
import { LegalShareholder } from '../../../../../../global/entities/Person';
import { createFieldArrayInputName } from '../../../../../../utils/field-array/create-field-array-input-name';
import {
  ObjectPathFn,
  TypedObjectAccessor,
} from '../../../../../../utils/types';
import { BuyOrderOrganization } from '../../../../../../utils/types/buy-order-relations';
import { useDeleteBuyOrderOrganization } from '../../../../../admin/buy-process/buy-order-edit/use-buy-order-edit';
import { useBuyProcessForm } from '../../../../buy-process-form-utils';
import { Stack } from '../../../../../stack/stack';
import { DangerButton } from '../../../../../danger-button/danger-button';
import { SWEDISH_SSN_PLACEHOLDER } from '../../../../../../global/constants';

export const LegalShareholderInput = ({
  fieldArrayName,
  index,
  remove,
  field,
}: FieldArrayInputProps<LegalShareholder>) => {
  const createName: ObjectPathFn<BuyOrderOrganization<LegalShareholder>> = (
    name: string,
  ) => createFieldArrayInputName(fieldArrayName, index, name);

  const getField: TypedObjectAccessor<BuyOrderOrganization<LegalShareholder>> = (name: string) => get(field, name);

  const { errors, register, control } = useFormContext();
  const { id } = useBuyProcessForm();
  const { handleRemoveOrganization } = useDeleteBuyOrderOrganization(
    id,
    getField('organizationId') as string,
  );

  function shouldAllowDeleteShareholder(index: number) {
    return index > 0;
  }

  return (
    <React.Fragment>
      <input
        ref={register()}
        name={createName('organizationId')}
        defaultValue={getField('organizationId')}
        hidden
      />
      <input
        ref={register()}
        name={createName('signerId')}
        defaultValue={getField('signerId')}
        hidden
      />

      <InputText
        margin="dense"
        name={createName('organizationName')}
        ref={register()}
        errors={errors}
        label="Org. namn"
        required
        defaultValue={getField('organizationName')}
      />

      <RHFInputText
        control={control}
        margin="dense"
        name={createName('organizationNumber')}
        errors={errors}
        label="Org. nummer"
        InputProps={{ inputComponent: MaskedSwedishSsnInput }}
        required
        defaultValue={getField('organizationNumber')}
      />

      <InputText
        margin="dense"
        name={createName('organizationAddress')}
        defaultValue={getField('organizationAddress')}
        ref={register()}
        errors={errors}
        label="Utdelningsadress"
        required
      />

      <Stack direction="row" gap={8}>
        <RHFInputText
          control={control}
          margin="dense"
          name={createName('organizationPostalCode')}
          defaultValue={getField('organizationPostalCode')}
          errors={errors}
          label="Postnummer"
          InputProps={{ inputComponent: MaskedSwedishPostNumberInput }}
          required
        />

        <InputText
          margin="dense"
          name={createName('organizationCity')}
          ref={register()}
          errors={errors}
          label="Ort"
          required
          defaultValue={getField('organizationCity')}
        />
      </Stack>

      <Stack
        direction="row"
        gap={8}
        style={{
          marginTop: '32px',
        }}
      >
        <InputText
          margin="dense"
          name={createName('firstName')}
          ref={register()}
          errors={errors}
          label="Företrädarens förnamn"
          required
          defaultValue={getField('firstName')}
        />

        <InputText
          margin="dense"
          name={createName('lastName')}
          ref={register()}
          errors={errors}
          label="Företrädarens efternamn"
          required
          defaultValue={getField('lastName')}
        />
      </Stack>

      <RHFInputText
        placeholder={SWEDISH_SSN_PLACEHOLDER}
        control={control}
        margin="dense"
        name={createName('socialSecurityNumber')}
        defaultValue={getField('socialSecurityNumber')}
        errors={errors}
        label="Företrädarens personnummer"
        InputProps={{ inputComponent: MaskedSwedishSsnInput }}
        required
      />

      <div
        style={{
          marginTop: '32px',
          maxWidth: '150px',
        }}
      >
        <InputText
          margin="dense"
          name={createName('numberShares')}
          ref={register()}
          errors={errors}
          label="Antal aktier"
          required
          defaultValue={getField('numberShares')}
        />
      </div>

      {shouldAllowDeleteShareholder(index) && (
        <DangerButton
          onClick={() => {
            remove(index);
            handleRemoveOrganization();
          }}
          style={{ alignSelf: 'flex-end', marginTop: '12px' }}
        >
          Radera Aktieägare
        </DangerButton>
      )}
    </React.Fragment>
  );
};
